import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, forkJoin, ReplaySubject, Subscription } from 'rxjs';
import { getPlatformLogoPath } from 'src/app/common/platformUtils';
import { AddPolicyDialogComponent } from 'src/app/components/add-policy-dialog/add-policy-dialog.component';
import { DuplicatePolicyDialogComponent } from 'src/app/components/duplicate-policy-dialog/duplicate-policy-dialog.component';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import {
  IWarningDialogInput,
  WarningDialogComponent,
} from 'src/app/components/warning-dialog/warning-dialog.component';
import { IntegrationModel } from 'src/app/models/api';
import { PolicyModel, PolicyType } from 'src/app/models/api/governance/policy.model';
import { getIntegrationStatusLabel } from 'src/app/models/api/integration.model';
import { NavigationParams } from 'src/app/models/navigation/navigationParams.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';

import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { QueryParamsService } from 'src/app/services/queryParams/query-params.service';

/**
 * This component has a routes state machine:
 *      You have 3 point of entering policies page (originalRoute state):
 *          1. With integration ID in the query params
 *              That will load the policies page of the specific integration
 *          2. Without any query params
 *              That will select the first integration in the list and add it to the query params.
 *
 *      In case of multiple integrations the user will have to choose from select drop down the integration he wishes to see its policies.
 */

@Component({
  selector: 'app-policies',
  templateUrl: './policies.component.html',
  styleUrls: ['./policies.component.scss'],
})
export class PoliciesComponent implements OnInit, OnDestroy {
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // integrations options and selection

  public integrations: IntegrationModel[];
  public integrationsListOptions: ISelectOption[] = [];
  public clearIntegrationSelection$ = new BehaviorSubject(undefined);
  public integrationSelectedOption$ = new ReplaySubject<ISelectOption>(1);
  public integrationNewOptions$ = new ReplaySubject<ISelectOption[]>();

  public reachedMaxNumberOfPolicies = false;

  public disabledAddPolicyButtonReason = '';
  public canDeletePolicy = false;
  public canDuplicatePolicy = false;

  // tabs
  public tabs: ISidepanelTab[] = [];
  public tabSelectedIndex = 0;

  public policies: PolicyModel[] = [];
  public defaultPolicy?: PolicyModel;

  // subscriptions
  private _integrationStoreSubscription$: Subscription;
  private _queryParamsSubscription$: Subscription;

  private _integrationId: string;

  private readonly _maxNumberOfPolicies = 10;

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _store: StoreService,
    private readonly _router: Router,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _notificationService: NotificationsService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _queryParamsService: QueryParamsService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _permissionsService: PermissionsService
  ) {}

  public get policyType(): typeof PolicyType {
    return PolicyType;
  }

  ngOnInit(): void {
    this.isLoading$.next(true);
    this._getIntegrations();
  }

  public ngOnDestroy(): void {
    this._integrationStoreSubscription$?.unsubscribe();
    this._queryParamsSubscription$?.unsubscribe();
  }

  public onIntegrationSelectedFromIntegrationList(selectedOption: ISelectOption) {
    this._router.navigate(['app/policies/integrations'], {
      queryParams: { integration: selectedOption.id },
    });
  }

  public duplicatePolicyClicked(policy: PolicyModel) {
    const dialogRef = this._matDialog.open(DuplicatePolicyDialogComponent, {
      data: policy,
      disableClose: false,
      autoFocus: false,
      panelClass: 'dynamic-size-popup-dialog-container',
    });
    dialogRef.afterClosed().subscribe((policyAdded: boolean) => {
      if (policyAdded) {
        this._refreshData();
      }
    });
  }

  public policyClicked(policy: PolicyModel) {
    this._router.navigate(['app/policies/integrations', policy.id], {
      queryParams: { integration: policy.integrationId },
      queryParamsHandling: 'merge',
    });
  }

  public addPolicyClicked() {
    const dialogRef = this._matDialog.open(AddPolicyDialogComponent, {
      data: this._integrationId,
      disableClose: false,
      autoFocus: false,
      panelClass: 'dynamic-size-popup-dialog-container',
    });
    dialogRef.afterClosed().subscribe((policyAdded: boolean) => {
      if (policyAdded) {
        this._refreshData();
      }
    });
  }

  public deleteCustomPolicy(policy: PolicyModel) {
    const dialogRef = this._matDialog.open(WarningDialogComponent, {
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
      data: {
        elementsNames: [policy.name],
        title: 'policyPage.deletePolicy',
        notificationMsgLabelPath: 'common.wasDeletedSuccessfully',
      } as IWarningDialogInput,
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading$.next(true);
        this._policiesHttpService.deleteCustomPolicy(this._integrationId, policy.id).subscribe({
          next: () => {
            this._notificationService.success('Policy was deleted successfully');
            this._getAllDataForIntegration(this._integrationId);
          },
          error: () => {
            this._notificationService.error('Failed to delete the policy');
            this.isLoading$.next(false);
          },
        });
      }
    });
  }

  private _handleQueryParams() {
    this._queryParamsSubscription$ = this._queryParamsService.toNavigate$.subscribe({
      next: (navigationParams: NavigationParams) => {
        this.isLoading$.next(true);
        if (navigationParams.integration) {
          this._integrationId = navigationParams.integration;
          this._updateIntegrationsList();
          this._integrationAutoSelect(navigationParams.integration);
          this._getAllDataForIntegration(navigationParams.integration);
        } else if (this.integrations.length > 0) {
          this._router.navigate(['app/policies/integrations'], {
            queryParams: {
              integration: this.integrations[0].id,
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        } else {
          this.isLoading$.next(false);
        }
      },
      error: (error) => {
        this._httpErrorHandlerService.handle(error);
      },
    });
  }

  private _getIntegrations() {
    this._integrationStoreSubscription$ = this._store.integrations$.subscribe({
      next: (integrations: IntegrationModel[]) => {
        this.integrations = integrations;
        this._handleQueryParams();
      },
    });
  }
  private _integrationAutoSelect(integrationId: string) {
    // inject to select component the selected integration manually since the user did not selected it from the integrations dropdown list
    const integrationIndex = this.integrationsListOptions.findIndex(
      (integrationOption) => integrationOption.id == integrationId
    );
    this.integrationSelectedOption$.next(this.integrationsListOptions[integrationIndex]);
  }

  private _getAllDataForIntegration(integrationId: string) {
    forkJoin([this._policiesHttpService.getIntegrationPolicies(integrationId)]).subscribe({
      next: (observablesResults) => {
        const policies = observablesResults[0];

        this.defaultPolicy = policies.find((policy) => policy.isDefault);
        this.policies = policies.filter((policy) => policy.isDefault === false);

        this.reachedMaxNumberOfPolicies = policies.length >= this._maxNumberOfPolicies;

        this._selectAddPolicyDisableReasonIfNeeded();

        this._managePermissions();

        this.isLoading$.next(false);
      },
      error: () => {
        this.isLoading$.next(false);
        this.policies = [];
        this.defaultPolicy = undefined;
        this._notificationService.error('An error occurred while trying to get the policies');
      },
    });
  }

  private _managePermissions() {
    this.canDeletePolicy = this._permissionsService.isAdmin();
    this.canDuplicatePolicy = this._permissionsService.isAdmin();
  }

  private _selectAddPolicyDisableReasonIfNeeded() {
    if (!this._permissionsService.isAdmin()) {
      this.disabledAddPolicyButtonReason = this._labelsProviderPipe.transform(
        'policyPage.disabledAddPolicyNoPermissionMsg'
      );
    } else if (this.reachedMaxNumberOfPolicies) {
      this.disabledAddPolicyButtonReason = this._labelsProviderPipe.transform(
        'policyPage.disabledAddPolicyReachedMaxMsg'
      );
    }
  }

  private _updateIntegrationsList() {
    const integrationOptions: ISelectOption[] = [];
    this.integrations.forEach((integration) => {
      const integrationStatus = getIntegrationStatusLabel(integration.connectionStatus);
      integrationOptions.push({
        id: integration.id,
        value: integration.id,
        displayName: integration.name + ' (' + integrationStatus + ')',
        iconPath: getPlatformLogoPath(integration.platform),
      } as ISelectOption);
    });
    this.integrationsListOptions = integrationOptions;
    this.integrationNewOptions$.next(this.integrationsListOptions); // notify select component to change its options immidiatly
  }

  private _refreshData() {
    this._store.updateAccountMinimalPolicies();
    this.isLoading$.next(true);
    this._getAllDataForIntegration(this._integrationId);
  }
}
